import { motion } from "framer-motion";
import { Sparkles, Rocket, BrainCircuit, Code2, Laptop, Quote } from "lucide-react";

// Ordered top → bottom: Year 4 (top step) down to Year 1 (bottom step).
// Each successive step is indented less, forming a descending staircase.
// Each year carries its own vibrant gradient for a colourful, stunning section.
const YEARS = [
  {
    key: "4",
    title: "Predictable Placements",
    Icon: Rocket,
    gradient: "from-[#0052FF] via-blue-500 to-sky-400",
    glow: "shadow-blue-500/25",
    img: "/general/placed.png",
    indent: "lg:ml-[36%]",
    bullets: [
      <>
        Finalize your <b className="font-bold text-gray-900">40+ project portfolio</b> and lock in your
        verified Tech Readiness Score.
      </>,
      <>
        Bypass standard job boards — our <b className="font-bold text-gray-900">Predictive Control Room</b>{" "}
        routes your profile directly to 1,800+ hiring partners.
      </>,
    ],
  },
  {
    key: "3",
    title: "Advanced Mastery",
    Icon: BrainCircuit,
    gradient: "from-[#FF6B00] via-orange-500 to-orange-400",
    glow: "shadow-orange-500/25",
    img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    indent: "lg:ml-[24%]",
    bullets: [
      <>
        Reach <b className="font-bold text-gray-900">junior-engineer proficiency</b>{" "}
        in your track — GenAI, cloud deployment, LLMs &amp; MLOps.
      </>,
      <>
        Defend <b className="font-bold text-gray-900">30+ advanced projects</b>{" "}
        through live, AI-driven Socratic vivas.
      </>,
    ],
  },
  {
    key: "2",
    title: "Applied Engineering",
    Icon: Laptop,
    gradient: "from-[#FF6B00] via-orange-500 to-[#0052FF]",
    glow: "shadow-aiorange/25",
    img: "/general/learnWithAI.png",
    indent: "lg:ml-[12%]",
    bullets: [
      <>
        Go deeper into your chosen track and land your{" "}
        <b className="font-bold text-gray-900">guaranteed internship</b> inside a
        real company.
      </>,
      <>
        Scale to <b className="font-bold text-gray-900">20+ verified projects</b>{" "}
        with industry AI tools (GitHub, Cursor) — earning recognized AI/ML skill
        badges.
      </>,
    ],
  },
  {
    key: "1",
    title: "The AI-Native Foundation",
    Icon: Code2,
    gradient: "from-[#0052FF] via-blue-500 to-[#FF6B00]",
    glow: "shadow-aiblue/25",
    img: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    indent: "lg:ml-0",
    bullets: [
      <>
        Choose your{" "}
        <b className="font-bold text-gray-900">specialization track</b> and{" "}
        <b className="font-bold text-gray-900">start coding from week one</b> with{" "}
        <b className="font-bold text-gray-900">Veda</b>, your 24/7 AI co-pilot.
      </>,
      <>
        Master programming fundamentals and architect your first{" "}
        <b className="font-bold text-gray-900">10 real-world projects</b> —
        launching a live portfolio from day one.
      </>,
    ],
  },
];

function StairCard({ year, isLast }) {
  const Icon = year.Icon;

  return (
    <div className={`relative ${year.indent} lg:max-w-[64%]`}>
      <motion.div
        initial={{ opacity: 0, x: -40, y: 16 }}
        whileInView={{ opacity: 1, x: 0, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
        className={`group relative flex flex-col sm:flex-row gap-5 bg-white rounded-2xl overflow-hidden shadow-xl ${year.glow} p-5 sm:p-6 pl-7 sm:pl-8 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300`}
      >
        {/* Gradient left accent bar */}
        <span
          aria-hidden="true"
          className={`absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b ${year.gradient}`}
        />
        {/* Soft gradient glow that intensifies on hover */}
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute -right-10 -top-10 w-40 h-40 rounded-full bg-gradient-to-br ${year.gradient} opacity-10 blur-2xl group-hover:opacity-20 transition-opacity`}
        />

        {/* Thumbnail (vibrant gradient placeholder + photo) */}
        <div className="relative shrink-0 h-36 w-full sm:w-40 rounded-xl overflow-hidden">
          <div className={`absolute inset-0 bg-gradient-to-br ${year.gradient}`} />
          <img
            src={year.img}
            alt={`Year ${year.key} — ${year.title}`}
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          {/* Gradient icon chip */}
          <div
            className={`absolute bottom-2.5 right-2.5 h-9 w-9 rounded-full bg-gradient-to-br ${year.gradient} ring-2 ring-white/80 flex items-center justify-center shadow-lg`}
          >
            <Icon className="h-4.5 w-4.5 text-white" strokeWidth={2.25} />
          </div>
        </div>

        {/* Content */}
        <div className="relative min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.15em] text-white shadow-sm bg-gradient-to-r ${year.gradient}`}
            >
              Year {year.key}
            </span>
            <h3 className="font-display font-black text-xl sm:text-2xl text-gray-900 tracking-tight">
              {year.title}
            </h3>
          </div>

          <ul className="mt-4 space-y-3">
            {year.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-3">
                <span
                  className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-gradient-to-br ${year.gradient}`}
                />
                <span className="text-[15px] leading-relaxed text-gray-700">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </motion.div>

      {/* Dashed staircase connector to the next (lower-left) step */}
      {!isLast && (
        <div
          aria-hidden="true"
          className="hidden lg:block absolute -bottom-12 left-10 h-14 w-px border-l-2 border-dashed border-gray-300 rotate-[32deg] origin-top"
        />
      )}
    </div>
  );
}

export default function SmruTimeline() {
  return (
    <section
      id="curriculum"
      className="relative overflow-hidden py-20 sm:py-28 bg-gradient-to-b from-white via-aisky/10 to-white"
    >
      {/* Ambient gradient blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-[28rem] h-[28rem] rounded-full bg-gradient-to-br from-aiblue/20 to-sky-300/20 blur-3xl" />
        <div className="absolute top-1/3 -right-24 w-[26rem] h-[26rem] rounded-full bg-gradient-to-br from-aiorange/15 to-orange-300/20 blur-3xl" />
        <div className="absolute -bottom-24 left-1/4 w-[26rem] h-[26rem] rounded-full bg-gradient-to-br from-aiorange/20 to-orange-300/20 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider text-white shadow-sm bg-gradient-to-r from-aiblue to-aiorange">
            <Sparkles className="h-3.5 w-3.5" />
            Your 4-Year Journey
          </span>
          <h2 className="mt-5 font-display font-black text-3xl sm:text-4xl lg:text-5xl text-gray-900 tracking-tight">
            The Transformation Roadmap{" "}
            <span className="bg-gradient-to-r from-[#0052FF] to-[#FF6B00] bg-clip-text text-transparent">
              From Classroom Coder to Elite AI Professional
            </span>
          </h2>
          <p className="mt-4 text-gray-500">
            Stop drifting through semesters. This is your hyper-focused career
            roadmap engineered by{" "}
            <span className="font-bold text-aiblue">AI.Prof</span> — every project
            you build secures your future.
          </p>
        </motion.div>

        {/* Staircase */}
        <div className="mt-16 lg:mt-20 space-y-10 lg:space-y-16">
          {YEARS.map((year, i) => (
            <StairCard
              key={year.key}
              year={year}
              isLast={i === YEARS.length - 1}
            />
          ))}
        </div>

        {/* Closing quote band */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="relative mt-16 overflow-hidden rounded-3xl bg-gradient-to-r from-aiblue to-aiorange p-8 sm:p-10 shadow-xl shadow-aiorange/20"
        >
          <div className="pointer-events-none absolute -top-16 -right-10 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
          <Quote className="relative h-8 w-8 text-white/60 mb-3 -scale-x-100" />
          <p className="relative font-display text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-snug">
            If you are going to invest{" "}
            <span className="underline decoration-white/50 decoration-2 underline-offset-4">
              four years of your life
            </span>
            , invest it in an ecosystem{" "}
            <span className="underline decoration-white/50 decoration-2 underline-offset-4">
              engineered for the AI-led future.
            </span>
          </p>
        </motion.div>
      </div>
    </section>
  );
}
